import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Copy } from 'lucide-react'
import { cn } from '@/lib/utils'

interface CopyButtonProps {
  value: string
  /** What is being copied, e.g. "Login 540861". Completes the accessible name and the confirmation. */
  label: string
  className?: string
}

type CopyState = 'idle' | 'copied' | 'failed'

export function CopyButton({ value, label, className }: CopyButtonProps) {
  const { t } = useTranslation()
  const [state, setState] = useState<CopyState>('idle')

  useEffect(() => {
    if (state === 'idle') return
    const timer = window.setTimeout(() => setState('idle'), 2000)
    return () => window.clearTimeout(timer)
  }, [state])

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setState('copied')
    } catch {
      setState('failed')
    }
  }

  const visible =
    state === 'copied' ? t('common.copied') : state === 'failed' ? t('common.copyFailed') : t('common.copy')

  return (
    <span className={cn('prod-copy', className)}>
      <button
        type="button"
        className={cn('prod-copy__btn', state === 'copied' && 'prod-copy__btn--done')}
        onClick={() => void copy()}
      >
        {state === 'copied' ? (
          <Check className="prod-copy__icon" aria-hidden="true" />
        ) : (
          <Copy className="prod-copy__icon" aria-hidden="true" />
        )}
        <span aria-hidden="true">{visible}</span>
        <span className="sr-only">
          {t('common.copy')} {label}
        </span>
      </button>
      <span className="sr-only" role="status">
        {state === 'copied' ? t('common.copiedValue', { label }) : state === 'failed' ? t('common.copyFailed') : ''}
      </span>
    </span>
  )
}
